import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faMinus, faPlane, faPlus, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { IAirportSelectOutput } from "libraries/component/airport-select/airport-select.component";
import { wenji } from 'libraries/interfaces/IWenji';
import { Yii } from "libraries/interfaces/Yii";
import { DateComponent } from "libraries/module/formbuilder/fields/date/date.component";
import { ICheckboxFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { IValidator } from "libraries/module/formbuilder/forms/utils/validators/IValidator";
import { FormBuilderService } from "libraries/service/formBuilder/formBuilder.service";
import { BtnStyleTypes } from 'libraries/ui_button/btn.component';
import _has from 'lodash-es/has';
import _toString from 'lodash-es/toString';
import _toUpper from 'lodash-es/toUpper';
import * as moment from 'moment';
import { timer } from "rxjs";
import _cloneDeep from "lodash-es/cloneDeep";
import { GenericFieldset, IGenericFieldset } from '../generic-fieldset.component';


export interface IFromToConfig {
    from: ICheckboxFieldSpecs;
    to: ICheckboxFieldSpecs;
}

export interface ICustomizedIcons {
    from: IconDefinition;
    to: IconDefinition;
    date: IconDefinition;
}

enum EFields {
    from = 'from',
    to = 'to',
    begin = 'begin',
    end = 'end',
    fromAirportOnly = 'fromAirportOnly',
    toAirportOnly = 'toAirportOnly',
    duration = 'duration',
    night = 'night',
    days = 'days',
}

export interface IReadonlyWhenSetRules{
    Oneway: IReadonlyWhenSetRule;
    Return: IReadonlyWhenSetRule;
    Multiple: IReadonlyWhenSetRule;
}

export interface IReadonlyWhenSetRule{
    from?: {
        airport: boolean;
        date: boolean;
    };
    to?: {
        airport: boolean;
        date: boolean;
    };
}

class ComponentWording {
    public onlyAirport: string = Yii.t('wui-iqba-js', 'Cet aéroport uniquement');
    public dateBegin: string = Yii.t('wui-shared-js', 'Date Aller');
    public dateEnd: string = Yii.t('wui-shared-js', 'Date Retour');
    public date: string = Yii.t('wui-shared-js', 'Date');
    public invertFromTo: string = Yii.t('wui-shared-js', 'Inverser les lieux de départ et de destination');
}

/**
 * Segment-fieldset
 * Generate a fieldset of needed fields
 * @extends GenericFieldset
 * @implements IGenericFieldset
 */
@Component({
    selector: 'segment-air-fieldset',
    templateUrl: './segment-air-fieldset.component.html',
    styleUrls: ['./segment-air-fieldset.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
})
export class SegmentAirFieldsetComponent extends GenericFieldset implements IGenericFieldset, OnInit, OnChanges {

    @Input()
    public onlyOneMonth: boolean;

    @Input()
    public showFieldLabelName: boolean = true;
    @Input()
    public readonlyWhenSetRules: IReadonlyWhenSetRules = {
        Oneway: null,
        Return: null,
        Multiple: null
    };
    @Input()
    public allowAirportOnlyCheckbox: boolean = true;
    @Input()
    public allowInvertFromTo: boolean = false;
    @Input()
    public customizedIcons: ICustomizedIcons;
    @Input()
    public autoPatchReturnDate: boolean = true;

    /** Readonly context can't be modified. Define the context of the fieldset */
    public readonly context: string = 'segment';
    public readonly eField: typeof EFields = EFields;

    public readonly today: Date = new Date();
    public btnType: typeof BtnStyleTypes = BtnStyleTypes;

    public dateValidator: IValidator[] = [
        {
            validator: Validators.required,
            errors: {
                required: {
                    when: 'touched',
                    message: Yii.t('wui-shared-js', 'Date requise'),
                }
            }
        }
    ];

    @ViewChild('returnDate')
    public readonly returnDate: DateComponent;

    public readonly iconPlane: IconDefinition = faPlane;
    public readonly iconPlus: IconDefinition = faPlus;
    public readonly iconMinus: IconDefinition = faMinus;
    public readonly wording: ComponentWording = new ComponentWording();

    constructor(
        public fb: FormBuilder,
        public formBuilderService: FormBuilderService,
        public changeDetectorRef: ChangeDetectorRef,
    ) {
        super(fb);
    }

    public ngOnInit(): void {
        if (this.config.context === 'air' && (!this.fieldsetData)) {
            this.setDirection();
            this.formBuilderService.setFirstOpening('searchAirTrip');
        } else if (this.config.context === 'air' && (this.fieldsetData)) {
            this.formBuilderService.setFirstOpening('searchAirTrip');
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (_has(changes, ['config', 'currentValue'])) {
            this.setDirection();
        }

        if (this.group && changes.fieldsetData && changes.fieldsetData.currentValue) {
            // tslint:disable-next-line:no-any
            this.fieldsetData = this.fieldsetData.map((dataItemArg: any, index: number) => {
                const dataItem: any = _cloneDeep(dataItemArg);
                let newItem: any;
                if (dataItem.from && dataItem.from.airport) {
                    // tslint:disable-next-line:no-parameter-reassignment
                    newItem = {
                        ...dataItem,
                        from: dataItem.from.airport.iata,
                        fromAirportOnly: true,
                    };
                    this.group.get([index, EFields.fromAirportOnly]).patchValue(true);
                } else {
                    this.group.get([index, EFields.fromAirportOnly]).patchValue(false);
                }
                if (dataItem.to && dataItem.to.airport) {
                    // tslint:disable-next-line:no-parameter-reassignment
                    newItem = {
                        ...(newItem || dataItem),
                        to: dataItem.to.airport.iata,
                        toAirportOnly: true
                    };
                    this.group.get([index, EFields.toAirportOnly]).patchValue(true);
                } else {
                    this.group.get([index, EFields.toAirportOnly]).patchValue(false);
                }
                if(dataItem.begin?.value){
                    dataItem.begin = dataItem.begin.value;
                }
                return newItem || dataItem;
            });
            this.group.patchValue(this.fieldsetData);
            this.changeDetectorRef.detectChanges();
        }
    }

    /*** METHODs ***/
    public setNumberFromBeginEndDate(index: number): void {
        const control: any = this.group;

        let begin = control.get([index, EFields.begin]);
        let end = control.get([index, EFields.end]);
        let night = control.get([index, EFields.duration, EFields.night]);

        let beginDate = moment(begin.value);
        const endDate = moment(end.value);

        if (begin.value && night.value && beginDate.diff(endDate, EFields.days) <= 0) {
            control.get([index, EFields.duration, EFields.night]).patchValue(-beginDate.diff(endDate, EFields.days));
        } else if (begin.value) {
            night.patchValue('1');
            end.patchValue(beginDate.add(1, EFields.days).toDate());
        } else if (control.get([index, EFields.end]).value && night.value) {
            begin = control.get([index, EFields.begin]);
            night = control.get([index, EFields.duration, EFields.night]);
            end = control.get([index, EFields.end]);

            beginDate = moment(end.value).subtract(night.value, EFields.days);

            begin.patchValue(beginDate.toDate());
        }
    }

    public setEndDateFromNumber(index: number): any {
        let output: string | Date;

        if (this.group.get([index, EFields.begin]).value && this.group.get([index, EFields.duration, EFields.night]).value) {
            const begin = this.group.get([index, EFields.begin]).value;
            const night = this.group.get([index, EFields.duration, EFields.night]).value;

            output = moment(begin).add(night, EFields.days).toDate();
            // #TODO à changer pour plusieurs réservations
            this.group.get([index, EFields.end]).patchValue(output);
        } else {
            this.group.get([index, EFields.end]).patchValue('');
        }
    }

    public setDateAndNightFromBegin(index: number): void {
        const control: any = this.group;
        let night = control.get([index, EFields.duration, EFields.night]);

        if (control.get([index, EFields.begin]).value && !night.value) {
            const begin = control.get([index, EFields.begin]);
            const end = control.get([index, EFields.end]);
            const endDate: string | Date = moment(begin.value).add(this.config.night, EFields.days).toDate();

            night.patchValue(this.config.night);
            end.patchValue(endDate);
        } else if (control.get([index, EFields.begin]).value && night.value) {
            const begin = control.get([index, EFields.begin]);
            night = control.get([index, EFields.duration, EFields.night]);
            const end = control.get([index, EFields.end]);

            const endDate: string | Date = moment(begin.value).add(night.value, EFields.days).toDate();

            end.patchValue(endDate);
        }
    }

    /**
     * test if external search is Return
     */
    public isReturnSearch(): boolean {
        const control = this.group;

        if (control.length === 2 &&
            control.get(['0', EFields.to]).value === control.get(['1', EFields.from]).value &&
            control.get(['0', EFields.from]).value === control.get(['1', EFields.to]).value
        ) {
            return true;
        }

        return false;
    }

    /**
     * seDirection
     * get Direction from config,
     * launched from ngOnInit
     */
    public setDirection(): void {
        switch (this.config.direction) {
            case 'Return':
                this.setReturn();
                break;
            case 'Oneway':
                this.firstField();
                break;
            default:
                break;
        }
    }


    /**
     * setReturn
     * set segment qtt to 2, 'Aller-retour'
     */
    public setReturn(): void {
        if (this.group.length === 1) {
            this.addFields();

            //TODO: EJ-2017-10-13: find a way to insure return airports
            const GROUP = this.group;
            const ctrl = GROUP.get('0');
            this.setReturnFrom(ctrl, EFields.to);
            this.setReturnTo(ctrl, EFields.from);

        }

        if (this.group.length > 2) {
            this.firstField();
            this.addFields();
        }
        //TODO : if segment[0].from & .begin has value, set them to segment[1].begin and from
    }

    /**
     * setReturnFrom
     * patch Value "from" of second segment from first one, oin case of Return direction
     */
    public setReturnFrom(_ctrl: any, _path: any): void {
        const ctrl = _ctrl.get(_path);
        const value = ctrl.value;
        if (value) {
            const ctrl2 = this.group.get('1');
            ctrl2.patchValue({from: _toUpper(value)});
        }
    }

    /**
     * setReturnTo
     * patch Value "to" of second segment from first one, oin case of Return drection
     */
    public setReturnTo(_ctrl: any, _path: any): void {
        const ctrl = _ctrl.get(_path);
        const value = ctrl.value;

        if (value) {
            const ctrl2 = this.group.get('1');
            ctrl2.patchValue({to: _toUpper(value)});
        }
    }

    /**
     * getDate of index value
     */
    public getDate(_index: number): any {
        if (_index === 0) {
            return this.today;
        }

        const GROUP = this.group;

        const segment = GROUP.get(_toString(_index));

        const dateField = segment.get(EFields.begin);
        if (dateField.value) {
            return dateField.value;
        }

    }

    /**
     * getLastDate, to ensure min Date in Multi-destination
     *
     */
    public getLastDate(_index: number): any {

        if (_index === 0) {
            return this.today;
        }

        const GROUP = this.group;
        for (let i = _index; i > 0; i--) {
            let segment = GROUP.get(_toString(i - 1));

            let dateField = segment.get(EFields.begin as unknown as string);

            if (dateField.value) {
                return dateField.value;
            }

        }

    }

    /**
     * getLastDate, to ensure min Date in Multi-destination
     *
     */
    public getNewDate(_index: number): any {
        if (_index === 0) {
            return this.today;
        } else {

            let lastDate = this.getLastDate(_index);

            //remove daysroundtrip LCBK => WUI
            let output = moment(lastDate).add(wenji.locobook.search.daysroundtrip, 'days');
            // return new Date(output);
            return output.toDate();
        }
    }

    /**
     * setReturnDate, set return Date auto when blur of date1
     *
     */
    public setReturnDate(): any {
        const GROUP: FormArray = this.group;
        const goDate: AbstractControl = GROUP.get(['0', EFields.begin]);

        if (goDate && (goDate.value || goDate.value !== "") && this.autoPatchReturnDate) {
            const backDate = this.getNewDate(1);
            if (!GROUP.get(['1', EFields.begin]).value) {
                GROUP.get(['1', EFields.begin]).patchValue(backDate);
            }
        }
    }

    public openAndFocusReturnDate(): void {
        this.returnDate.asyncToggle();
    }

    public patchSegment(index: number, parameter: EFields, locationCode: IAirportSelectOutput): void {
        const output: IAirportSelectOutput = !locationCode ?
        {
            code: "",
            isAirportOnly: false
        } : locationCode;
        this.group.get([index, parameter]).patchValue(output.code);
        this.group.get([index, `${parameter === EFields.from ? 'fromAirportOnly' : 'toAirportOnly'}`]).patchValue(output.isAirportOnly);
        if (this.config.direction === 'Return') {
            this.group.get([1, `${parameter === EFields.from ? EFields.to : EFields.from}`]).patchValue(output.code);
            this.group.get([1, `${parameter === EFields.from ? 'toAirportOnly' : 'fromAirportOnly'}`]).patchValue(output.isAirportOnly);
        }
    }

    public allCustomizedIconsAreDefined(): boolean {
        return Boolean(this.customizedIcons?.from && this.customizedIcons?.to && this.customizedIcons?.date);
    }

    public invertFromTo(segmentIndex: number): void {

        if (!this.allowInvertFromTo) {
            return;
        }

        const curFormGroup: FormGroup = this.group.get([0]) as FormGroup;
        if (this.config.direction === 'Oneway') {

            const originalFromRule: { date: boolean; airport: boolean } = {...this.readonlyWhenSetRules.Oneway.from};
            const originalToRule: { date: boolean; airport: boolean } = {...this.readonlyWhenSetRules.Oneway.to};

            // Required to allow display update (???)
            if (this.readonlyWhenSetRules.Oneway.from) {
                this.readonlyWhenSetRules.Oneway.from.airport = false;
            }
            if (this.readonlyWhenSetRules.Oneway.to) {
                this.readonlyWhenSetRules.Oneway.to.airport = false;
            }

            const firstSegment: any = curFormGroup.getRawValue();
            this.group.get([0, EFields.from]).patchValue(firstSegment.to);
            this.group.get([0, EFields.to]).patchValue(firstSegment.from);

            // Required to allow display update (???)
            timer().subscribe((): void => {
                this.readonlyWhenSetRules.Oneway = {
                    from: originalToRule,
                    to: originalFromRule
                };
            });
        } else if (this.config.direction === 'Return') {
            //patch hidden segment in case of A/R form
            const firstSegment: any = curFormGroup.value;
            const secondSegment: any = this.group.get([1]).value;
            this.group.get([0, EFields.from]).patchValue(secondSegment.to);
            this.group.get([0, EFields.to]).patchValue(secondSegment.from);
            this.group.get([1, EFields.from]).patchValue(firstSegment.to);
            this.group.get([1, EFields.to]).patchValue(firstSegment.from);
        } else if (this.config.direction === 'Multiple') {
            const from: string = this.group.get([segmentIndex, EFields.from]).value;
            const to: string = this.group.get([segmentIndex, EFields.to]).value;

            this.group.get([segmentIndex, EFields.from]).patchValue(to);
            this.group.get([segmentIndex, EFields.to]).patchValue(from);
        }

        this.changeDetectorRef.markForCheck();
    }

    private setNightFromEndOnChange(index: number): void {
        const control: any = this.group;
        const end = control.get([index, EFields.end]);

        if (end.value) {
            const night = control.get([index], EFields.duration, EFields.night);
            const begin = control.get([index], EFields.begin);

            if (begin.value) {
                night.patchValue(-begin.diff(end, EFields.days));
            }
        }
    }
}
